import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';

interface SpeedometerSegment {
  from: number;
  to: number;
  color: string;
}

@Component({
  selector: 'app-inbuilt-speedometer-v2',
  templateUrl: './inbuilt-speedometer-v2.component.html',
  styleUrl: './inbuilt-speedometer-v2.component.css',
  standalone: false
})
export class InbuiltSpeedometerV2Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('speedometerContainer', { static: true }) private speedometerContainer!: ElementRef<HTMLElement>;

  minValue: number = 0;
  maxValue: number = 372000000;
  currentValue: number = 151800000;
  ticks: number[] = [0, 124000000, 248000000, 372000000];
  segments: SpeedometerSegment[] = [
    { from: 0, to: 150000000, color: '#efb46b' },
    { from: 150000000, to: 372000000, color: '#78c89f' }
  ];
  private resizeObserver: ResizeObserver | null = null;
  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private lastSelectedFiltersSignature = '';
  loading = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    // Keep speedometer fully static as requested.
    this.minValue = 0;
    this.maxValue = 372000000;
    this.currentValue = 319000000;
    this.ticks = [0, 124000000, 248000000, 372000000];

    const firstSegmentEnd = this.minValue + ((this.maxValue - this.minValue) * 0.4);
    this.segments = [
      { from: this.minValue, to: firstSegmentEnd, color: '#efb46b' },
      { from: firstSegmentEnd, to: this.maxValue, color: '#78c89f' }
    ];

    if (this.maxValue <= this.minValue) {
      this.maxValue = this.minValue + 1;
    }

    this.currentValue = this.clamp(this.currentValue, this.minValue, this.maxValue);
    this.ticks = this.getSanitizedTicks();
    this.segments = this.getSanitizedSegments();

    this.selectedFilters = this.config?.selectedFilters || {};
    this.lastSelectedFiltersSignature = JSON.stringify(this.selectedFilters || {});
    this.subscribeToFilterEvents();
    this.callSpeedometerApi();
  }

  ngAfterViewInit(): void {
    this.renderSpeedometer();
    this.resizeObserver = new ResizeObserver(() => {
      this.renderSpeedometer();
    });
    this.resizeObserver.observe(this.speedometerContainer.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        const nextFilters = event?.selectedFilters || {};
        const nextSignature = JSON.stringify(nextFilters);
        if (nextSignature === this.lastSelectedFiltersSignature) {
          return;
        }

        this.selectedFilters = nextFilters;
        this.lastSelectedFiltersSignature = nextSignature;
        this.callSpeedometerApi();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callSpeedometerApi(): void {
    this.loading = true;

    const payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'speedometer',
      download: false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters)
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          console.log('Speedometer API response:', response);
        },
        error: (error: any) => {
          console.error('Speedometer API error:', error);
        }
      });
  }

  get currentPercentage(): number {
    if (this.maxValue <= this.minValue) {
      return 0;
    }
    return ((this.currentValue - this.minValue) / (this.maxValue - this.minValue)) * 100;
  }

  formatCompact(value: number): string {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (absValue >= 1000000000) {
      return `${sign}$${this.formatScaled(absValue / 1000000000)}B`;
    }
    if (absValue >= 1000000) {
      return `${sign}$${this.formatScaled(absValue / 1000000)}M`;
    }
    if (absValue >= 1000) {
      return `${sign}$${this.formatScaled(absValue / 1000)}K`;
    }

    return `${sign}$${this.formatScaled(absValue)}`;
  }

  private formatScaled(value: number): string {
    const rounded = Number(value.toFixed(1));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  private toNumber(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private getSanitizedTicks(): number[] {
    const allTicks = [this.minValue, ...this.ticks, this.maxValue]
      .map((tick: number) => this.clamp(tick, this.minValue, this.maxValue));

    return [...new Set(allTicks)].sort((a: number, b: number) => a - b);
  }

  private getSanitizedSegments(): SpeedometerSegment[] {
    const normalized = this.segments
      .map((segment: SpeedometerSegment) => ({
        from: this.clamp(segment.from, this.minValue, this.maxValue),
        to: this.clamp(segment.to, this.minValue, this.maxValue),
        color: this.normalizeColor(segment.color)
      }))
      .filter((segment: SpeedometerSegment) => segment.to > segment.from)
      .sort((a: SpeedometerSegment, b: SpeedometerSegment) => a.from - b.from);

    if (normalized.length === 0) {
      return [{ from: this.minValue, to: this.maxValue, color: '#9ca3af' }];
    }

    return normalized;
  }

  private normalizeColor(value: any): string {
    const normalized = String(value ?? '')
      .trim()
      .replace(/^color\s*:\s*/i, '');

    return normalized || '#9ca3af';
  }

  private getRenderedSegments(): SpeedometerSegment[] {
    const sorted = [...this.segments]
      .map((segment: SpeedometerSegment) => ({
        from: this.clamp(segment.from, this.minValue, this.maxValue),
        to: this.clamp(segment.to, this.minValue, this.maxValue),
        color: this.normalizeColor(segment.color)
      }))
      .filter((segment: SpeedometerSegment) => segment.to > segment.from)
      .sort((a: SpeedometerSegment, b: SpeedometerSegment) => a.from - b.from);

    if (!sorted.length) {
      return [{ from: this.minValue, to: this.maxValue, color: '#78c89f' }];
    }

    const output: SpeedometerSegment[] = [];
    let cursor = this.minValue;

    sorted.forEach((segment: SpeedometerSegment) => {
      if (segment.to <= cursor) {
        return;
      }

      const from = Math.max(segment.from, cursor);
      if (from > cursor) {
        output.push({
          from: cursor,
          to: from,
          color: output.length ? output[output.length - 1].color : sorted[0].color
        });
      }

      output.push({ from, to: segment.to, color: segment.color });
      cursor = segment.to;
    });

    if (cursor < this.maxValue) {
      output.push({ from: cursor, to: this.maxValue, color: output[output.length - 1].color });
    }

    return output;
  }

  private renderSpeedometer(): void {
    const host = this.speedometerContainer?.nativeElement;
    if (!host) {
      return;
    }

    d3.select(host).selectAll('*').remove();

    const width = Math.max(18, host.clientWidth || 18);
    const centerX = width / 2;
    const radius = width * 0.36;
    const labelPad = width * 0.09;
    const topPad = width * 0.04;
    const bottomPad = width * 0.06;
    const centerY = topPad + labelPad + radius;
    const height = centerY + bottomPad;
    const arcThickness = radius * 0.24;

    // D3 arc angles use 0 at 12 o'clock. Use -PI/2 to PI/2 for a left-to-right top semicircle.
    const startAngle = -Math.PI / 2;
    const endAngle = Math.PI / 2;

    const valueScale = d3
      .scaleLinear()
      .domain([this.minValue, this.maxValue])
      .range([startAngle, endAngle]);

    const svg = d3
      .select(host)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const chart = svg.append('g').attr('transform', `translate(${centerX}, ${centerY})`);

    const baseArc = d3
      .arc<any>()
      .innerRadius(radius - arcThickness)
      .outerRadius(radius)
      .startAngle(startAngle)
      .endAngle(endAngle);

    chart
      .append('path')
      .attr('d', baseArc as any)
      .attr('fill', '#374151');

    const renderedSegments = this.getRenderedSegments();

    renderedSegments.forEach((segment: SpeedometerSegment, index: number) => {
      const anglePadding = 0.0015;
      const rawStart = valueScale(segment.from);
      const rawEnd = valueScale(segment.to);
      const start = index === 0 ? startAngle : Math.max(startAngle, rawStart - anglePadding);
      const end = index === renderedSegments.length - 1 ? endAngle : Math.min(endAngle, rawEnd + anglePadding);

      const segmentArc = d3
        .arc<any>()
        .innerRadius(radius - arcThickness)
        .outerRadius(radius)
        .startAngle(start)
        .endAngle(end);

      chart
        .append('path')
        .attr('d', segmentArc as any)
        .attr('fill', segment.color)
        .attr('stroke', segment.color)
        .attr('stroke-width', radius * 0.002);
    });

    const tickGroup = chart.append('g').attr('class', 'tick-group');
    const tickLength = radius * 0.08;

    this.ticks.forEach((tickValue: number) => {
      const angle = valueScale(tickValue);
      const xDir = Math.sin(angle);
      const yDir = -Math.cos(angle);

      const labelRadius = radius + tickLength * 2.35;
      const isMinTick = Math.abs(tickValue - this.minValue) < 0.0001;
      const isMaxTick = Math.abs(tickValue - this.maxValue) < 0.0001;
      const minLabelOffset = radius * 0.03;
      const maxLabelOffset = radius * 0.02;
      const maxRadiusBoost = radius * 0.04;
      const effectiveLabelRadius = isMaxTick ? (labelRadius + maxRadiusBoost) : labelRadius;
      const labelX = (effectiveLabelRadius * xDir) + (isMinTick ? minLabelOffset : isMaxTick ? maxLabelOffset : 0);
      const labelY = (effectiveLabelRadius * yDir) + (isMaxTick ? radius * 0: 0);
      const labelAnchor = isMinTick ? 'start' : isMaxTick ? 'end' : 'middle';

      tickGroup
        .append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', labelAnchor)
        .attr('dominant-baseline', 'middle')
        .style('font-size', `${Math.max(0.62, width / 46 / 16)}rem`)
        .style('fill', '#111827')
        .text(this.formatCompact(tickValue));
    });

    const needleGroup = chart.append('g').attr('class', 'needle-group');

    const hubRadiusOuter = radius * 0.14;
    const hubRadiusInner = radius * 0.095;
    const semiCircleArc = d3
      .arc<any>()
      .innerRadius(hubRadiusInner)
      .outerRadius(hubRadiusOuter)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    needleGroup
      .append('path')
      .attr('d', semiCircleArc as any)
      .attr('fill', '#3f3f46');

    const needleLength = radius * 0.84;
    const needleBaseWidth = radius * 0.055;
    const needleBaseX = radius * 0.07;

    needleGroup
      .append('polygon')
      .attr('points', `${needleLength},0 ${needleBaseX},${-needleBaseWidth} ${needleBaseX},${needleBaseWidth}`)
      .attr('fill', '#ffffff')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', radius * 0.008)
      .attr('stroke-linejoin', 'round');

    needleGroup
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', radius * 0.028)
      .attr('fill', '#4b5563');

    const valueRatio = (this.currentValue - this.minValue) / (this.maxValue - this.minValue);
    const targetAngleDegrees = -180 + (180 * valueRatio);

    needleGroup
      .transition()
      .duration(1500)
      .ease(d3.easeCubicOut)
      .attrTween('transform', () => {
        const interpolator = d3.interpolateNumber(-180, targetAngleDegrees);
        return (time: number) => `rotate(${interpolator(time)})`;
      });
  }
}
